<template>
  <div
    id="addPlayer"
    :class="[
      'w-100 py-2',
      $style.searchWrapper
    ]"
  >
    <b-container v-if="isSearchEnabled">
      <b-row>
        <b-col
          :cols="6"
          class="d-flex align-items-center"
        >
          <BaseTextH2 class="m-0">
            {{ $t('ADD-PLAYERS') }}
          </BaseTextH2>
        </b-col>
        <b-col
          :cols="6"
          class="d-flex align-items-center justify-content-end"
        >
          <font-awesome-icon
            icon="search"
            size="lg"
            class="mr-1"
            :class="$style.searchIcon"
          />
          <BaseInput
            v-model="search"
            :placeholder="$t('search-player')"
            type="text"
            is-errors-hidden
            :class-name="$style.noBorder"
            @keyup.native="handleSearch"
          />
        </b-col>
      </b-row>
    </b-container>
    <div
      v-if="round.matches.length > 1"
      :class="$style.matchesWrapper"
      class="mt-2"
    >
      <div :class="$style.horizontalScrollWrapper">
        <div
          id="matchScroll"
          :class="$style.horizontalScrollContent"
        >
          <div :class="$style.horizontalScrollItemWrapper">
            <div
              :class="[ $style.allPlayersButton, !selectedTeamId && $style.isActive ]"
              @click="handleChangeTeam({teamId: false})"
            >
              <b-img
                :class="$style.leagueImage"
                class="pb-2"
                fluid
                :src="round.season.league.image_url"
              />
              {{ $t('all-players') }}
            </div>
          </div>
          <template
            v-for="match in matches"
          >
            <div
              :key="`addPlayerMatch-${match.id}-${match.team1.id}`"
              :class="$style.horizontalScrollItemWrapper"
            >
              <BaseTeamButton
                :name="match.team1.display_name"
                :image-url="match.team1.image_url"
                :is-active="selectedTeamId === match.team1.id"
                :team2name="match.team2.display_name"
                class="mx-2"
                @click="handleChangeTeam({teamId: match.team1.id})"
              />
            </div>
            <div
              :key="`addPlayerMatch-${match.id}-${match.team2.id}`"
              :class="$style.horizontalScrollItemWrapper"
            >
              <BaseTeamButton
                :name="match.team2.display_name"
                :image-url="match.team2.image_url"
                :team2name="match.team1.display_name"
                :is-active="selectedTeamId === match.team2.id"
                class="mx-2"
                :is-team-at-home="false"
                @click="handleChangeTeam({teamId: match.team2.id})"
              />
            </div>
          </template>
        </div>
      </div>
      <div
        :class="[ $style.scrollArrow, $style.left ]"
        @mousedown="() => handleMouseDown(-1)"
        @mouseup="handleMouseUp"
      >
        <font-awesome-icon
          icon="chevron-left"
          size="2x"
        />
      </div>
      <div
        :class="[ $style.scrollArrow, $style.right ]"
        @mousedown="() => handleMouseDown(1)"
        @mouseup="handleMouseUp"
      >
        <font-awesome-icon
          icon="chevron-right"
          size="2x"
        />
      </div>
    </div>
    <div
      v-else
      class="d-flex justify-content-center pt-2 pb-3"
    >
      <BaseButton
        :variant="!selectedTeamId ? 'orange' : 'outline-orange'"
        left-rounded
        font-size="small"
        @click="handleChangeTeam({teamId: false})"
      >
        <span class="px-2">{{ $t('all-players') }}</span>
      </BaseButton>
      <BaseButton
        :variant="(selectedTeamId === round.matches[0].team1.id) ? 'orange' : 'outline-orange'"
        class="px-1"
        font-size="small"
        @click="handleChangeTeam({teamId: round.matches[0].team1.id})"
      >
        <span class="px-2">{{ round.matches[0].team1.name[$i18n.locale] }}</span>
      </BaseButton>
      <BaseButton
        :variant="(selectedTeamId === round.matches[0].team2.id) ? 'orange' : 'outline-orange'"
        right-rounded
        font-size="small"
        @click="handleChangeTeam({teamId: round.matches[0].team2.id})"
      >
        <span class="px-2">{{ round.matches[0].team2.name[$i18n.locale] }}</span>
      </BaseButton>
    </div>

    <div :class="$style.playersWrapper">
      <template v-if="isAthletesLoading">
        <BaseLoading />
      </template>
      <template v-else-if="!athletes.length">
        <BaseAlert>
          {{ $t('there-are-no-player-for-selected-team') }}
        </BaseAlert>
      </template>
      <template v-else>
        <BaseAthleteNoneCard
          v-if="gameType === 'summer'"
          :is-selected="!! isNoAthletePick"
        >
          <template slot="right">
            <BaseButton
              :variant="isNoAthletePick ? 'orange' : 'outline-orange'"
              :is-disabled="!! isNoAthletePick"
              font-size="small"
              is-rounded
              @click="handleNoAthletePick"
            >
              <span v-if="!isNoAthletePick">{{ $t('pick') }}</span>
              <span v-else>{{ $t('picked') }}</span>
            </BaseButton>
          </template>
        </BaseAthleteNoneCard>
        <template v-for="athlete in athletes">
          <div
            v-if="!!selectedAthletes.find(({id}) => athlete.id === id)"
            :key="`athlete-${athlete.id}`"
          >
            <ThePickAthleteCard
              :id="athlete.id"
              :round-id="round.id"
              :name="athlete.name"
              :avatar="athlete.image"
              :team="athlete.team"
              :position="athlete.position"
              :score="athlete.score"
              :is-selected="!! selectedAthletes.find(({id}) => athlete.id === id)"
              :is-player-select-enabled="pickedPlayersCount < maximumPickedPlayersCount"
              :maximum-picked-players-count="maximumPickedPlayersCount"
              :is-athlete-assignment-processing="isAthleteAssignmentProcessing === athlete.id"
              @onAthletePick="$emit('onAthletePick', { athleteId: athlete.id })"
              @onAthletePickRemove="$emit('onAthletePickRemove', { athleteId: athlete.id, roundId: round.id })"
            />
          </div>
        </template>
        <template v-for="athlete in athletes">
          <div
            v-if="!selectedAthletes.find(({id}) => athlete.id === id)"
            :key="`athlete-${athlete.id}`"
          >
            <ThePickAthleteCard
              :id="athlete.id"
              :round-id="round.id"
              :name="athlete.name"
              :avatar="athlete.image"
              :team="athlete.team"
              :position="athlete.position"
              :score="athlete.score"
              :is-selected="!! selectedAthletes.find(({id}) => athlete.id === id)"
              :is-player-select-enabled="pickedPlayersCount < maximumPickedPlayersCount"
              :maximum-picked-players-count="maximumPickedPlayersCount"
              :is-athlete-assignment-processing="isAthleteAssignmentProcessing === athlete.id"
              @onAthletePick="$emit('onAthletePick', { athleteId: athlete.id, roundId: round.id })"
            />
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import VueScrollTo from 'vue-scrollto'
import { mapActions, mapState } from 'vuex'
import { MATCH_STATUSES } from '@/store/modules/matches/matchDetail'
import BaseAlert from '@/components/Atoms/BaseAlert'
import BaseInput from '@/components/Atoms/Form/BaseInput'
import BaseLoading from '@/components/Atoms/BaseLoading'
import BaseTeamButton from '@/components/Atoms/BaseTeamButton'
import ThePickAthleteCard from '@/components/Molecules/ThePickAthleteCard'
import BaseTextH2 from '@/components/Atoms/Typography/BaseTextH2'
import BaseButton from '@/components/Atoms/BaseButton'
import BaseAthleteNoneCard from '@/components/Atoms/BaseAthleteNoneCard'

export default {
  components: {
    BaseAthleteNoneCard,
    BaseButton,
    BaseTextH2,
    BaseAlert,
    BaseInput,
    BaseLoading,
    BaseTeamButton,
    ThePickAthleteCard,
  },
  props: {
    round: {
      type: Object,
      required: true,
    },
    maximumPickedPlayersCount: {
      type: Number,
      required: false,
      default: 8,
    },
    isSearchEnabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    leagueType: {
      type: String,
      required: false,
      default: null,
    },
    isAthleteAssignmentProcessing: {
      type: Number,
      required: false,
      default: null,
    },
    selectedAthletes: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      search: this.searchString,
    }
  },
  computed: {
    ...mapState({
      isRoundLoading: state => state['rounds/index'].isRoundLoading,
      selectedTeamId: state => state['rounds/assignAthleteIntoRound'].selectedTeamId,
      isAthletesLoading: state => state['rounds/assignAthleteIntoRound'].isAthletesLoading,
      searchString: state => state['rounds/assignAthleteIntoRound'].search,
      gameType: state => state['game/type'].type,
    }),
    matches() {
      return this.round.matches.filter(({ status }) => status === MATCH_STATUSES.upcoming)
    },
    athletes() {
      return this.$store.getters['rounds/assignAthleteIntoRound/getAndSearchAthletesForRound']({
        roundId: this.round.id,
        lang: this.$i18n.locale,
      })
    },
    pickedPlayersCount() {
      return this.selectedAthletes.length
    },
    isNoAthletePick() {
      return this.$store.getters['rounds/athletesInRound/getIsNoAthletePickForRound'](this.round.id)
    },
  },
  watch: {
    searchString(newValue) {
      this.search = newValue
    },
  },
  created() {
    this.fetchRoundAthletes({
      roundId: this.round.id,
    })
  },
  methods: {
    ...mapActions({
      fetchTeams: 'rounds/teams/fetchTeams',
      handleChangeTeam: 'rounds/assignAthleteIntoRound/handleChangeTeam',
      fetchRoundAthletes: 'rounds/assignAthleteIntoRound/fetchRoundAthletes',
      handleAthletesSearch: 'rounds/assignAthleteIntoRound/handleAthletesSearch',
      fetchSelectedAthletes: 'rounds/athletesInRound/fetchSelectedAthletes',
      openTeamCompleteModal: 'profile/inviteUrl/openTeamCompleteModal',
      openSummerTeamCompleteModal: 'rounds/summer/predictionCompleted/openModal',
      setNoAthletePickForRound: 'rounds/assignAthleteIntoRound/setNoAthletePickForRound',
    }),
    handleSearch(e) {
      if (e && e.target) {
        this.handleAthletesSearch({
          search: e.target.value,
        })
      }
    },
    handleMouseDown(amount) {
      this.interval = setInterval(() => {
        document.getElementById('matchScroll').scrollLeft += amount
      }, 5)
    },
    handleMouseUp() {
      clearInterval(this.interval)
    },
    handleNoAthletePick() {
      VueScrollTo.scrollTo('#content', 500, {
        container: 'body',
      })
      this.setNoAthletePickForRound({
        roundId: this.round.id,
      }).then(() => {
        this.fetchSelectedAthletes({
          roundId: this.round.id,
        })
        if (this.leagueType === 'summer') {
          this.openSummerTeamCompleteModal({
            roundId: this.round.id,
          })
        } else {
          this.openTeamCompleteModal({
            roundId: this.round.id,
          })
        }
      })
    },
  },
}
</script>

<style module lang="scss">
  @import "../../../../../assets/scss/variables";
  .searchWrapper {
    background-color: $color-bleached-silk;
  }
  .searchIcon {
    color: $color-wet-weather;
  }
  .noBorder {
    border: none !important;
  }

  .leagueImage {
    max-width: 32px !important;
    max-height: 32px !important;
  }
  .matchesWrapper {
    position: relative;
    height: 65px;
  }
  .teamsWrapper {
    position: relative;
    height: 113px;
  }
  .horizontalScrollWrapper {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 1;
    overflow: hidden;
    left: 0;
    &:after{
      z-index: 2;
      position: absolute;
      content: '';
      width: 1px;
      height: 100%;
      left: 0;
      top: 0;
      background: white;
      box-shadow: 0 0 35px 25px $color-bleached-silk;
    }
    &:before{
      z-index: 2;
      position: absolute;
      content: '';
      width: 1px;
      height: 100%;
      right: 0;
      top: 0;
      background: white;
      box-shadow: 0 0 35px 25px white;
    }
  }
  .horizontalScrollContent {
    height: 100%;
    position: relative;
    display: flex;
    width: auto;
    overflow: auto;
    white-space: nowrap;
    padding: 0 25px;
    padding-bottom: 20px;
    -webkit-overflow-scrolling: touch;
  }
  .horizontalScrollItemWrapper {
    display: inline-block;
    height: 100%;
    padding: 0 .5rem;
  }
  .scrollArrow {
    position: absolute;
    height: 100%;
    z-index: 3;
    display: flex;
    cursor: pointer;
    align-items: center;
    padding-bottom: 20px;
    &:hover {
      svg {
        transition: all .3s ease-in-out;
      }
    }
    &.left {
      left: 5px;
      &:hover {
        svg {
          transform: scale(1.2) translateX(2px);
        }
      }
    }
    &.right {
      right: 5px;
      &:hover {
        svg {
          transform: scale(1.2) translateX(-2px);
        }
      }
    }
  }

  .allPlayersButton {
    cursor: pointer;
    display: flex;
    height: 100%;
    justify-content: center;
    align-items: center;
    padding: 0 1rem;
    border-radius: .5rem;
    border: 2px solid $color-tomato;
    transition: all .25s ease-in-out;
    font-size: $font-size-small;
    color: $color-tomato;
    img {
      margin-right: .5rem;
    }
    &.isActive {
      background-color: $color-tomato;
      color: $color-white;
    }
    &:hover {
      background-color: darken($color-tomato, 5%);
      color: $color-white;
    }
  }

  .playersWrapper {
    min-height: 350px;
  }

  .teamsWrapper {
    max-height: 100px;
  }
</style>
